import { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import {
  DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS,
  DataModelTransitionGraph,
  DataModelTransitionGraphApiResponse,
  DataModelTransitionGraphFieldItem,
  DataModelTransitionGraphParams,
  toDataModelTransitionGraphSetting,
} from '../domain/dataModelTransitionGraph';
import {
  normalizeGraphFieldsForFetch,
  resolveDataModelTransitionGraphFields,
  resolveDataModelTransitionGraphFieldsForSearch,
} from '../domain/dataModelTransitionGraphFields';
import { buildModelTransitionGraphParamsFromCheckedRows } from '../domain/modelTransition/mainField';

import { dataModelTransitionDataSearchConditionSelector } from '../redux/server/dataModelTransition';
import {
  DataModelTransitionGraphActionCreators,
  DataModelTransitionGraphActionTypes,
  FetchDataModelTransitionGraphAction,
  SearchDataModelTransitionGraphAction,
  SetDataModelTransitionGraphFieldsAction,
  dataModelTransitionGraphSettingSelector,
} from '../redux/server/dataModelTransitionGraph';
import { ModelTransitionReportsSettingActionCreators } from '../redux/ui/modelTransitionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

/** 並列取得の1項目分が成功した結果。field はどのリクエストかの識別用 */
type GraphFieldFetchSuccess = {
  field: string;
  response: DataModelTransitionGraphApiResponse;
};

/** 並列取得の1項目分が失敗した結果。throw せず返すことで他項目の成功分を残せる */
type GraphFieldFetchFailure = {
  field: string;
  error: unknown;
};

/** fetchGraphByFieldSafely の戻り値。成功/失敗でキーが排他的（response か error） */
type GraphFieldFetchResult = GraphFieldFetchSuccess | GraphFieldFetchFailure;

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchSuccess => 'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchFailure => 'error' in result;

/**
 * グラフ API 用クエリを作る。複数項目は Saga 側で field を1つずつ付け替えて呼ぶため、ここでは除く。
 */
const toGraphApiQueryParams = (
  params: DataModelTransitionGraphParams,
  field: string
) => {
  const { field: _field, fields: _fields, ...apiParams } = params;
  return { ...apiParams, field };
};

/**
 * API レスポンス配列から Redux 用 `{ setting, graphList }` を組み立てる。
 */
const buildDataModelTransitionGraph = (
  responses: DataModelTransitionGraphApiResponse[],
  fields: string[]
): DataModelTransitionGraph => {
  const firstSetting = responses.at(0)?.setting;
  if (!firstSetting) {
    throw new Error('Graph API response is empty');
  }

  const graphList: DataModelTransitionGraphFieldItem[] = fields.flatMap(
    (field, index) => {
      const response = responses.at(index);
      if (!response) {
        return [];
      }
      return [{ field, data: response.data }];
    }
  );

  return {
    setting: toDataModelTransitionGraphSetting(firstSetting),
    graphList,
  };
};

/**
 * 単一 field の API 呼び出し。失敗時は throw する（Safely 側で catch する）。
 */
function* fetchGraphByField(
  api: Api,
  params: DataModelTransitionGraphParams,
  field: string
) {
  const apiRequestConfig = buildConfig(toGraphApiQueryParams(params, field));
  const response: AxiosResponse<DataModelTransitionGraphApiResponse> =
    yield call(api.get, '/data/kiTransition/graph', apiRequestConfig);
  return response.data;
}

/** fetchGraphByField の try/catch 版。all で並列取得するとき、1本失敗しても他を続行するため */
function* fetchGraphByFieldSafely(
  api: Api,
  params: DataModelTransitionGraphParams,
  field: string
): Generator<
  unknown,
  GraphFieldFetchResult,
  DataModelTransitionGraphApiResponse
> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    return { field, response };
  } catch (error: unknown) {
    return { field, error };
  }
}

/**
 * 検索条件を元に推移グラフのデータを取得する。
 * params.fields / field から項目数を決め、項目ごとに API を並列呼び出しする。
 * 一部だけ失敗した場合は成功分だけ graphList に載せ、全失敗時のみ renew する。
 */
export function* fetchDataModelTransitionGraphSaga(
  api: Api,
  action: FetchDataModelTransitionGraphAction
) {
  const { params } = action.payload;
  const fields = resolveDataModelTransitionGraphFields(params);

  // 前回の閉じアニメーションが中断されフラグがfalseのまま残っていても開けるよう、取得開始時に有効化する
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionGraphAnimationEnabledAction(
      true
    )
  );

  yield put(
    DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphRequestAction()
  );

  const results: GraphFieldFetchResult[] = yield all(
    fields.map((field) => call(fetchGraphByFieldSafely, api, params, field))
  );

  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield put(
      DataModelTransitionGraphActionCreators.renewDataModelTransitionGraphAction()
    );
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionGraphAnimationEnabledAction(
        false
      )
    );
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  const graph = buildDataModelTransitionGraph(
    successfulResults.map((result) => result.response),
    successfulResults.map((result) => result.field)
  );

  yield put(
    DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphSuccessAction(
      graph
    )
  );

  // 失敗した項目があっても成功分は表示を維持（部分失敗時は warn のみ。全失敗時のみ handleErrorSaga）
  if (failedResults.length > 0) {
    console.warn(
      `[dataModelTransitionGraphSaga] 推移グラフの一部項目の取得に失敗しました: ${failedResults
        .map((result) => result.field)
        .join(', ')}`
    );
  }
}

/**
 * 表示項目配列を指定して fetch を再 dispatch する共通処理。
 */
function* refetchDataModelTransitionGraphWithFields(fields: string[]) {
  const searchCondition: DataModelTransitionGraphParams | undefined =
    yield* customSelect(dataModelTransitionGraphSettingSelector);

  if (!searchCondition) {
    console.warn(
      '[dataModelTransitionGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました'
    );
    return;
  }

  const normalizedFields = normalizeGraphFieldsForFetch(
    fields,
    DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
  );

  yield put(
    DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphAction({
      ...searchCondition,
      fields: normalizedFields,
    })
  );
}

/**
 * チェック行の推移グラフデータを取得する（推移グラフボタン押下時）。
 * 初回はアウト1本。既に表示済みのときは graphList の項目を維持して行 ID だけ更新する。
 * @param action Action
 */
export function* searchDataModelTransitionGraphSaga(
  action: SearchDataModelTransitionGraphAction
) {
  const { checkedRowIds } = action.payload;

  const searchCondition = yield* customSelect(
    dataModelTransitionDataSearchConditionSelector
  );

  if (searchCondition === undefined) {
    // グラフボタンはテーブルデータ表示後に出る前提。未取得なら何もできない
    console.warn(
      '[dataModelTransitionGraphSaga] テーブル検索条件が未設定のためグラフ取得をスキップしました'
    );
    return;
  }

  const existingGraphParams = yield* customSelect(
    dataModelTransitionGraphSettingSelector
  );

  const fields =
    resolveDataModelTransitionGraphFieldsForSearch(existingGraphParams);

  yield put(
    DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphAction(
      buildModelTransitionGraphParamsFromCheckedRows(
        searchCondition.mainField,
        checkedRowIds,
        searchCondition,
        fields
      )
    )
  );
}

/**
 * 表示グラフ項目を配列で一括変更して再取得する。
 */
export function* setDataModelTransitionGraphFieldsSaga(
  action: SetDataModelTransitionGraphFieldsAction
) {
  yield* refetchDataModelTransitionGraphWithFields(action.payload.fields);
}

/** FETCH_DATA_MODEL_TRANSITION_GRAPH を監視（お気に入り適用・再取得の本体） */
function* handleFetchDataModelTransitionGraphSaga(api: Api) {
  yield takeLatest(
    DataModelTransitionGraphActionTypes.FETCH_DATA_MODEL_TRANSITION_GRAPH,
    fetchDataModelTransitionGraphSaga,
    api
  );
}

/**
 * 検索系 Action を監視（初回表示・項目セレクト変更）。
 */
function* handleSearchDataModelTransitionGraphSaga() {
  yield takeLatest(
    DataModelTransitionGraphActionTypes.SEARCH_DATA_MODEL_TRANSITION_GRAPH,
    searchDataModelTransitionGraphSaga
  );
  yield takeLatest(
    DataModelTransitionGraphActionTypes.SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS,
    setDataModelTransitionGraphFieldsSaga
  );
}

export function* dataModelTransitionGraphSaga(context: { api: Api }) {
  yield fork(handleFetchDataModelTransitionGraphSaga, context.api);
  yield fork(handleSearchDataModelTransitionGraphSaga);
}
